import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import type { MeResponse } from "@/features/auth/types";
import { inventoryApi } from "@/features/inventory/api";
import type { Product, ProductCategory } from "@/features/inventory/types";
import { STOCK_STATE_LABELS, type StockState } from "@/features/catalog/types";
import { apiErrorMessage } from "@/lib/api/errors";
import { hasFeature } from "@/lib/permissions/can";

import { articlesApi, type LotListParams } from "./api";
import type {
  ArticleSection,
  FilterChip,
  LotFilter,
  LotState,
  ProductLot,
  ProductUnit,
  ProductUnitPayload,
} from "./types";

// ---------------------------------------------------------------------------
// Modules
// ---------------------------------------------------------------------------

/** Espace Articles : une seule page pour le catalogue (catalogue avancé, ADR 0062). */
export function usesArticlesSpace(me: MeResponse | null): boolean {
  return hasFeature(me, "catalog_plus");
}

/** Lots et dates de péremption (ADR 0062). Le serveur exige aussi le catalogue. */
export function usesStockLots(me: MeResponse | null): boolean {
  return hasFeature(me, "stock_lots") && hasFeature(me, "catalog_plus");
}

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

export interface SectionDef {
  value: ArticleSection;
  label: string;
  /** Phrase courte sous le titre de la page. */
  hint: string;
}

const ALL_SECTIONS: SectionDef[] = [
  { value: "articles", label: "Articles", hint: "Catalogue, prix et stock." },
  { value: "variantes", label: "Variantes", hint: "Toutes les déclinaisons des modèles." },
  { value: "categories", label: "Catégories", hint: "Familles d'articles." },
  { value: "attributs", label: "Attributs", hint: "Axes des variantes et leurs valeurs." },
  { value: "unites", label: "Unités", hint: "Pièce, paire, boîte, flacon…" },
  { value: "marques", label: "Marques", hint: "Articles, modèles et stock par marque ; renommer ou fusionner." },
  { value: "lots", label: "Lots & péremption", hint: "Lots en stock et dates de péremption." },
];

/** Sections visibles pour la clinique active (chaque module en ajoute). */
export function articleSections(me: MeResponse | null): SectionDef[] {
  const variants = hasFeature(me, "product_variants");
  return ALL_SECTIONS.filter((s) => {
    if (s.value === "variantes" || s.value === "attributs") return variants;
    if (s.value === "lots") return usesStockLots(me);
    if (s.value === "marques") return hasFeature(me, "brands_labels") && hasFeature(me, "catalog_plus");
    return true;
  });
}

/** Section demandée par l'URL, ou « Articles » si elle n'existe pas ici. */
export function resolveSection(raw: string | null | undefined, sections: SectionDef[]): ArticleSection {
  const found = sections.find((s) => s.value === raw);
  return found ? found.value : "articles";
}

/**
 * Adresse de l'espace Articles qui remplace une ancienne adresse du catalogue
 * (favoris) : `/catalogue?vue=variantes` → section Variantes, `/categories` →
 * section Catégories ; les autres paramètres (recherche, filtres) suivent.
 */
export function articlesUrlFor(
  legacy: "catalogue" | "categories",
  search: string,
): string {
  const params = new URLSearchParams(search);
  if (legacy === "categories") {
    params.set("section", "categories");
  } else if (params.get("vue") === "variantes") {
    params.set("section", "variantes");
  }
  params.delete("vue");
  const query = params.toString();
  return query ? `/articles?${query}` : "/articles";
}

// ---------------------------------------------------------------------------
// Filters of the list
// ---------------------------------------------------------------------------

/** Paramètres d'URL des filtres de la liste (en plus de la recherche `q`). */
export const ARTICLE_FILTER_PARAMS = [
  "category",
  "brand",
  "price_min",
  "price_max",
  "stock_state",
  "is_active",
  "kind",
] as const;

/**
 * Pastilles des filtres actifs, dans l'ordre de lecture : recherche, puis
 * catégorie, marque, prix, stock, statut, type. Chaque pastille dit ce qu'elle
 * filtre et se retire d'un clic.
 */
export function activeFilterChips(
  get: (key: string) => string,
  categories: Pick<ProductCategory, "id" | "name" | "path">[] = [],
): FilterChip[] {
  const chips: FilterChip[] = [];
  const q = get("q").trim();
  if (q) chips.push({ params: ["q"], label: `« ${q} »` });
  const category = get("category");
  if (category) {
    const found = categories.find((c) => c.id === category);
    const name = found ? (found.path ?? found.name) : "Catégorie";
    chips.push({ params: ["category"], label: name });
  }
  if (get("brand")) chips.push({ params: ["brand"], label: get("brand") === "__none__" ? "Sans marque" : `Marque : ${get("brand")}` });
  const min = get("price_min");
  const max = get("price_max");
  if (min || max) {
    const label = min && max ? `${min} – ${max}` : min ? `≥ ${min}` : `≤ ${max}`;
    chips.push({ params: ["price_min", "price_max"], label: `Prix ${label}` });
  }
  const stock = get("stock_state") as StockState;
  if (stock && stock in STOCK_STATE_LABELS) {
    chips.push({ params: ["stock_state"], label: STOCK_STATE_LABELS[stock] });
  }
  const active = get("is_active");
  if (active === "true" || active === "false") {
    chips.push({ params: ["is_active"], label: active === "true" ? "Actifs" : "Inactifs" });
  }
  const kind = get("kind");
  if (kind === "template" || kind === "simple") {
    chips.push({ params: ["kind"], label: kind === "template" ? "Modèles" : "Articles simples" });
  }
  return chips;
}

/** Valeurs à écrire dans l'URL pour retirer une pastille (et revenir en page 1). */
export function clearChip(chip: FilterChip): Record<string, null> {
  return Object.fromEntries([...chip.params, "page"].map((k) => [k, null]));
}

// ---------------------------------------------------------------------------
// Selection and bulk actions
// ---------------------------------------------------------------------------

export function toggleSelected(selected: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(selected);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

/** Tout cocher / tout décocher la page affichée. */
export function togglePage(selected: ReadonlySet<string>, pageIds: string[]): Set<string> {
  const next = new Set(selected);
  const all = pageIds.length > 0 && pageIds.every((id) => next.has(id));
  for (const id of pageIds) {
    if (all) next.delete(id);
    else next.add(id);
  }
  return next;
}

export function pageSelectionState(
  selected: ReadonlySet<string>,
  pageIds: string[],
): "none" | "some" | "all" {
  const count = pageIds.filter((id) => selected.has(id)).length;
  if (count === 0) return "none";
  return count === pageIds.length ? "all" : "some";
}

function csvCell(value: string | number | null | undefined): string {
  const text = value === null || value === undefined ? "" : String(value);
  return /[";\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * Export CSV de la sélection (séparateur « ; », lisible par Excel en
 * français). Le stock n'est exporté que si la clinique le suit.
 */
export function selectionCsv(rows: Product[], opts: { withStock: boolean }): string {
  const header = [
    "Référence",
    "Libellé",
    "Marque",
    "Catégorie",
    "Unité",
    "Prix de vente",
    "Prix d'achat",
    ...(opts.withStock ? ["Stock"] : []),
    "Statut",
  ];
  const lines = rows.map((p) =>
    [
      p.sku,
      p.name,
      p.brand,
      p.category_display,
      p.unit_name ?? "Pièce",
      p.sale_price,
      p.cost_price ?? "",
      ...(opts.withStock ? [p.is_template ? "" : p.quantity_in_stock] : []),
      p.is_active ? "Actif" : "Inactif",
    ]
      .map(csvCell)
      .join(";"),
  );
  // BOM : Excel reconnaît l'UTF-8 (accents).
  return "﻿" + [header.join(";"), ...lines].join("\r\n");
}

/** Articles de la sélection que l'agence peut modifier (les autres sont écartés). */
export function editableSelection(rows: Product[], canEdit: (p: Product) => boolean): Product[] {
  return rows.filter(canEdit);
}

/** Bilan d'une action groupée. */
export function bulkSummary(done: number, failed: number, verb: string): string {
  const s = done > 1 ? "s" : "";
  const head = `${done} article${s} ${verb}${s}`;
  return failed > 0 ? `${head}, ${failed} en échec.` : `${head}.`;
}

// ---------------------------------------------------------------------------
// Keyboard
// ---------------------------------------------------------------------------

/** La touche « / » ne doit pas voler la frappe d'un champ en cours de saisie. */
export function isTypingTarget(el: EventTarget | null): boolean {
  if (!el || typeof (el as HTMLElement).tagName !== "string") return false;
  const node = el as HTMLElement;
  const tag = node.tagName.toLowerCase();
  return tag === "input" || tag === "textarea" || tag === "select" || node.isContentEditable === true;
}

// ---------------------------------------------------------------------------
// Units and lots: display rules
// ---------------------------------------------------------------------------

/** « Boîte de 6 (6 × Pièce) » ; une unité simple garde son nom. */
export function unitDescription(unit: Pick<ProductUnit, "name" | "base_unit_name" | "factor">): string {
  if (!unit.base_unit_name) return unit.name;
  return `${unit.name} (${unit.factor ?? 1} × ${unit.base_unit_name})`;
}

/** Unités courantes proposées tant que le référentiel ne les a pas. */
export const STARTER_UNITS = ["Pièce", "Paire", "Boîte", "Flacon"] as const;

export function missingStarterUnits(units: Pick<ProductUnit, "name">[]): string[] {
  const have = new Set(units.map((u) => u.name.trim().toLowerCase()));
  return STARTER_UNITS.filter((name) => !have.has(name.toLowerCase()));
}

export const LOT_STATE_LABELS: Record<LotState, string> = {
  expired: "Périmé",
  soon: "À surveiller",
  ok: "Valide",
  none: "Sans date",
};

export function lotTone(state: LotState, daysLeft: number | null): "danger" | "warning" | "success" | "neutral" {
  if (state === "expired") return "danger";
  if (state === "soon") return daysLeft !== null && daysLeft <= 30 ? "danger" : "warning";
  if (state === "ok") return "success";
  return "neutral";
}

/** « Périmé depuis 3 j », « Périme aujourd'hui », « Dans 20 j », « — ». */
export function expiryLabel(daysLeft: number | null): string {
  if (daysLeft === null) return "—";
  if (daysLeft < 0) return `Périmé depuis ${-daysLeft} j`;
  if (daysLeft === 0) return "Périme aujourd'hui";
  return `Dans ${daysLeft} j`;
}

/** Catégorie d'optique (pictogramme lunettes sur la fiche) : sans accent ni casse. */
export function isOpticalCategory(name: string | null | undefined): boolean {
  const key = (name ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
  return /(monture|verre|lunette|lentille|solaire|optique)/.test(key);
}

/** Couleur d'un type de mouvement dans le journal d'un article. */
export function movementTone(
  type: string,
): "success" | "neutral" | "info" | "danger" | "primary" | "warning" {
  switch (type) {
    case "in":
    case "return":
      return "success";
    case "loss":
      return "danger";
    case "adjustment":
      return "info";
    case "transfer":
      return "primary";
    case "supplier_return":
      return "warning";
    default:
      return "neutral";
  }
}

/** Libellé des lots d'une sortie : « B2611 (−2) · hors lot (−1) ». */
export function movementLotsLabel(
  lots: { lot: string | null; lot_number?: string | null; quantity: number }[] | undefined,
): string {
  if (!lots || lots.length === 0) return "—";
  return lots
    .map((l) => `${l.lot ? l.lot_number : "hors lot"} (${l.quantity > 0 ? "+" : ""}${l.quantity})`)
    .join(" · ");
}

/** Sens d'un mouvement manuel pour les lots : entrée (lot à saisir) ou sortie. */
export function movementDirection(type: string, quantity: number): "in" | "out" | "none" {
  if (type === "transfer") return "none";
  if (type === "out" || type === "loss") return "out";
  if (type === "adjustment") return quantity > 0 ? "in" : quantity < 0 ? "out" : "none";
  return "in";
}

/** « 18/11/2026 », ou « — » sans date. */
export function formatExpiry(date: string | null | undefined): string {
  if (!date) return "—";
  const [y, m, d] = date.split("-");
  return `${d}/${m}/${y}`;
}

/** Adresse de la fiche : l'espace Articles quand il est actif, sinon le catalogue. */
export function articleHref(me: MeResponse | null, id: string): string {
  return usesArticlesSpace(me) ? `/articles/${id}` : `/catalogue/${id}`;
}

/** Paramètres du registre pour un filtre d'état. */
export function lotFilterParams(filter: LotFilter): Pick<LotListParams, "state" | "within"> {
  if (filter === "expired") return { state: "expired" };
  if (filter === "none") return { state: "none" };
  if (filter === "30" || filter === "60" || filter === "90") {
    return { state: "expiring", within: Number(filter) };
  }
  return {};
}

/**
 * Lots disponibles pour répartir une sortie déjà enregistrée : ce que la
 * sortie leur a pris leur est rendu le temps du choix.
 */
export function lotsBeforeMovement<T extends Pick<ProductLot, "id" | "quantity">>(
  lots: T[],
  taken: { lot: string | null; quantity: number }[] | undefined,
): T[] {
  return lots.map((lot) => {
    const out = (taken ?? []).find((t) => t.lot === lot.id);
    return out ? { ...lot, quantity: lot.quantity - out.quantity } : lot;
  });
}

/** Lots d'une sortie choisis à la main : jamais plus que le lot ni que la sortie. */
export function validatePicks(
  picks: Record<string, number>,
  lots: Pick<ProductLot, "id" | "lot_number" | "quantity">[],
  total: number,
): string | null {
  let sum = 0;
  for (const lot of lots) {
    const q = picks[lot.id] ?? 0;
    if (q < 0 || !Number.isInteger(q)) return "Quantité entière positive attendue.";
    if (q > lot.quantity) return `Le lot ${lot.lot_number} n'a que ${lot.quantity} unité(s).`;
    sum += q;
  }
  if (sum > total) return `${sum} unités choisies pour une sortie de ${total}.`;
  return null;
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

const KEY = "articles";

export function useUnits(enabled: boolean) {
  return useQuery({
    queryKey: [KEY, "units"],
    queryFn: articlesApi.units,
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

function useRefreshUnits() {
  const qc = useQueryClient();
  return async () => {
    await qc.invalidateQueries({ queryKey: [KEY, "units"] });
    await qc.invalidateQueries({ queryKey: ["inventory"] });
  };
}

export function useSaveUnit(id?: string) {
  const refresh = useRefreshUnits();
  return useMutation({
    mutationFn: (payload: ProductUnitPayload) =>
      id ? articlesApi.updateUnit(id, payload) : articlesApi.createUnit(payload),
    onSuccess: async () => {
      await refresh();
      toast.success(id ? "Unité mise à jour." : "Unité créée.");
    },
  });
}

export function useToggleUnit() {
  const refresh = useRefreshUnits();
  return useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      articlesApi.updateUnit(id, { is_active }),
    onSuccess: refresh,
    onError: (err) => toast.error(apiErrorMessage(err, "Mise à jour de l'unité impossible.")),
  });
}

export function useDeleteUnit() {
  const refresh = useRefreshUnits();
  return useMutation({
    mutationFn: articlesApi.deleteUnit,
    onSuccess: async () => {
      await refresh();
      toast.success("Unité supprimée.");
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Suppression impossible.")),
  });
}

/** Crée d'un geste les unités courantes absentes. */
export function useCreateStarterUnits() {
  const refresh = useRefreshUnits();
  return useMutation({
    mutationFn: async (names: string[]) => {
      for (const name of names) await articlesApi.createUnit({ name });
      return names.length;
    },
    onSuccess: async (count) => {
      await refresh();
      toast.success(`${count} unité${count > 1 ? "s" : ""} ajoutée${count > 1 ? "s" : ""}.`);
    },
    onError: async (err) => {
      await refresh();
      toast.error(apiErrorMessage(err, "Création des unités impossible."));
    },
  });
}

export function useLots(params: LotListParams, enabled = true) {
  return useQuery({
    queryKey: [KEY, "lots", params],
    queryFn: () => articlesApi.lots(params),
    enabled,
    placeholderData: (prev) => prev,
  });
}

export function useLotAlerts(enabled: boolean) {
  return useQuery({
    queryKey: [KEY, "lot-alerts"],
    queryFn: articlesApi.lotAlerts,
    enabled,
    staleTime: 60 * 1000,
  });
}

export function useProductLots(productId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [KEY, "product-lots", productId],
    queryFn: () => articlesApi.productLots(productId!),
    enabled: enabled && !!productId,
  });
}

function useRefreshLots() {
  const qc = useQueryClient();
  return async () => {
    await qc.invalidateQueries({ queryKey: [KEY] });
    await qc.invalidateQueries({ queryKey: ["inventory"] });
  };
}

export function useDeclareLot() {
  const refresh = useRefreshLots();
  return useMutation({
    mutationFn: articlesApi.declareLot,
    onSuccess: async (lot) => {
      await refresh();
      toast.success(`Lot ${lot.lot_number} déclaré.`);
    },
  });
}

export function useUpdateLot(id: string) {
  const refresh = useRefreshLots();
  return useMutation({
    mutationFn: (payload: { lot_number?: string; expiry_date?: string | null }) =>
      articlesApi.updateLot(id, payload),
    onSuccess: async () => {
      await refresh();
      toast.success("Lot corrigé.");
    },
  });
}

export function useReassignLots(movementId: string) {
  const refresh = useRefreshLots();
  return useMutation({
    mutationFn: (picks: { lot: string; quantity: number }[]) =>
      articlesApi.reassignLots(movementId, picks),
    onSuccess: async () => {
      await refresh();
      toast.success("Lots de la sortie enregistrés.");
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Choix des lots impossible.")),
  });
}

/**
 * Action groupée sur la sélection : une mise à jour par article, à la suite
 * (le serveur garde ses contrôles un par un). Rend le nombre de réussites et
 * d'échecs, sans s'arrêter au premier échec.
 */
export function useBulkUpdateArticles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      ids,
      payload,
    }: {
      ids: string[];
      payload: { is_active?: boolean; category?: string };
    }) => {
      let done = 0;
      let failed = 0;
      for (const id of ids) {
        try {
          await inventoryApi.update(id, payload);
          done += 1;
        } catch {
          failed += 1;
        }
      }
      return { done, failed };
    },
    onSettled: async () => {
      await qc.invalidateQueries({ queryKey: ["inventory"] });
    },
  });
}
